import type { ModuleFileReference } from './dto.order';
import { DOCUMENT_CATEGORY } from '../../../../constants/constants';
import type { DocumentCategory } from '../../../../constants/constants';

/**
 * Categories where only the latest document (by createdDatetime) should be kept.
 * All other categories are passed through unfiltered.
 */
const LATEST_ONLY_CATEGORIES: DocumentCategory[] = [
  DOCUMENT_CATEGORY.DRIVER_LICENSE,
  DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK,
];

/**
 * Deduplicates moduleFileReferenceDtoList so that categories in
 * LATEST_ONLY_CATEGORIES retain only the most recent document (by createdDatetime).
 * Documents in other categories are kept as-is.
 *
 * @param files - The raw file reference list from the API
 * @returns Deduplicated file list
 */
export const deduplicateFileReferences = (
  files: ModuleFileReference[] | undefined
): ModuleFileReference[] => {
  if (!files?.length) return [];

  const latestByCategory = new Map<string, ModuleFileReference>();
  const otherFiles: ModuleFileReference[] = [];

  for (const file of files) {
    const category = file.documentCategory ?? '';

    if (!LATEST_ONLY_CATEGORIES.includes(category as DocumentCategory)) {
      otherFiles.push(file);
      continue;
    }

    const existing = latestByCategory.get(category);
    if (!existing || (file.createdDatetime ?? '') > (existing.createdDatetime ?? '')) {
      latestByCategory.set(category, file);
    }
  }

  return [...otherFiles, ...latestByCategory.values()];
};

/**
 * Filters moduleFileReferenceDtoList by document category, returning the
 * full ModuleFileReference objects.
 *
 * @param files - The file reference list from the API
 * @param category - The document category to filter by
 * @returns Filtered array of ModuleFileReference
 */
export const getFilesByCategory = (
  files: ModuleFileReference[] | undefined | null,
  category: DocumentCategory
): ModuleFileReference[] => {
  if (!files?.length) return [];
  return files.filter((file) => file.documentCategory === category);
};

/** Files grouped by their document category in a single O(n) pass. */
export type FilesByCategory = Partial<Record<string, ModuleFileReference[]>>;

/**
 * Groups moduleFileReferenceDtoList by documentCategory in a single pass.
 * Call once at the top level and pass the result down to avoid repeated loops.
 *
 * @param files - The file reference list from the API
 * @returns Object keyed by category, each value an array of matching files
 */
export const groupFilesByCategory = (
  files: ModuleFileReference[] | undefined | null
): FilesByCategory => {
  if (!files?.length) return {};

  const grouped: FilesByCategory = {};
  for (const file of files) {
    const key = file.documentCategory ?? 'UNCATEGORIZED';
    (grouped[key] ??= []).push(file);
  }
  return grouped;
};

/** Shape expected by FileUpload's initialFiles prop */
export type InitialFileReference = {
  fileId: string;
  fileName: string;
  sfdcFileIdentifier?: string;
  sfdcDeliveryIdentifier?: string;
};

/**
 * Maps an already-filtered array of ModuleFileReferences to the
 * {fileId, fileName} shape required by FileUpload, filtering out entries
 * without both an id and a name.
 *
 * Pair with `groupFilesByCategory` to avoid re-filtering the full list:
 * ```ts
 * mapToInitialFiles(filesByCategory[DOCUMENT_CATEGORY.X] ?? [])
 * ```
 *
 * @param files - Pre-filtered file references for a single category
 * @returns Array of {fileId, fileName} for FileUpload initialFiles
 */
export const mapToInitialFiles = (files: ModuleFileReference[]): InitialFileReference[] =>
  files
    .map((file) => ({
      fileId: file.filehubIdentifier || file.fileIdentifier || '',
      fileName: file.fileNameOriginal || file.fileNameSanitized || '',
      sfdcFileIdentifier: file.sfdcFileIdentifier ?? undefined,
      sfdcDeliveryIdentifier: file.sfdcDeliveryIdentifier ?? undefined,
    }))
    .filter((file) => Boolean(file.fileId && file.fileName));
